import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient, type InfiniteData } from "@tanstack/react-query";
import { API_ENDPOINTS } from "@/shared/constants/api-endpoints";
import { useChatStore } from "@/store/chat";
import { ChatApiError, chatRequest } from "../api/client";
import { chatKey, chatQueryOptions } from "../api/chat-query";
import { directoryKey, directoryQueryOptions } from "../api/use-directory";
import { sessionQueryOptions } from "../api/use-session";
import type {
  ConnectionState,
  RealtimeContextValue,
} from "../api/realtime-context";
import {
  RealtimeConnection,
  type TransportState,
} from "../lib/realtime-connection";
import { SyncQueue } from "../lib/sync-queue";
import type { DirectoryPage, Identity, Session } from "../types";
import { useTypingMembers } from "./use-typing-indicator";

/**
 * While a socket is open, re-read the session over HTTP this often. The server's
 * glow-session record lives 60 s and only authenticated HTTP refreshes it (the
 * relay's periodic re-check deliberately does not), so an idle open chat must
 * keep it alive; revocation still closes the socket within the 60 s TTL.
 */
export const SESSION_KEEPALIVE_MS = 30_000;

/** The session owns transport; selecting a conversation only changes its room. */
export function useWorkspaceRealtime(
  identity: Identity,
  chatId: string | null,
  enabled: boolean,
): RealtimeContextValue {
  const client = useQueryClient();
  const { clientId, memberId, adventureId, name } = identity;
  const scope = `${clientId}:${memberId}`;
  const [status, setStatus] = useState<{
    scope: string;
    connection: ConnectionState;
  }>({
    scope,
    connection: "Connecting",
  });
  const typing = useTypingMembers(
    `${scope}:${chatId ?? ""}`,
    chatId ?? "",
    memberId,
  );
  const handlers = useRef({
    receive: typing.receive,
    clear: typing.clear,
    clearAssistants: typing.clearAssistants,
  });
  const selected = useRef(chatId);
  const controller = useRef<RealtimeConnection | null>(null);
  const sync = useRef<SyncQueue | null>(null);

  useEffect(() => {
    handlers.current = {
      receive: typing.receive,
      clear: typing.clear,
      clearAssistants: typing.clearAssistants,
    };
    selected.current = chatId;
    controller.current?.setChat(chatId);
    sync.current?.request({ directory: true, chatId });
    const inactive = client
      .getQueryCache()
      .findAll({ queryKey: ["webchat", clientId, memberId] })
      .filter((query) => !query.isActive() && query.queryKey[3] !== chatId)
      .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt);
    for (const query of inactive.slice(8))
      client.removeQueries({ queryKey: query.queryKey, exact: true });
  }, [
    chatId,
    typing.receive,
    typing.clear,
    typing.clearAssistants,
    client,
    clientId,
    memberId,
  ]);

  useEffect(() => {
    if (!enabled) return;
    const currentIdentity = { clientId, memberId, adventureId, name };
    let stopped = false;
    let sessionExpired = false;
    let transport: TransportState = "connecting";
    let caughtUp = false;
    let directoryFailed = false;
    let failedRoom: string | null = null;
    // The queue is serialized; retain which operation actually failed when a
    // work item includes both directory and conversation synchronization.
    let syncingRoom: string | null = null;
    let accessLost = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let keepAlive: ReturnType<typeof setInterval> | undefined;
    const inactive = () => stopped || sessionExpired;
    const setKeepAlive = (open: boolean) => {
      if (open && keepAlive === undefined) {
        keepAlive = setInterval(() => {
          if (!inactive())
            void client
              .fetchQuery(sessionQueryOptions())
              .catch(() => undefined);
        }, SESSION_KEEPALIVE_MS);
      } else if (!open && keepAlive !== undefined) {
        clearInterval(keepAlive);
        keepAlive = undefined;
      }
    };
    const syncFailed = () =>
      directoryFailed ||
      (failedRoom !== null && failedRoom === selected.current);
    const update = () => {
      if (stopped) return;
      const connection: ConnectionState =
        transport === "session-expired"
          ? "Sign in to reconnect"
          : transport === "access-denied" || accessLost
            ? "Chat access unavailable"
            : !navigator.onLine || transport === "offline"
              ? "Offline"
              : syncFailed() || transport === "reconnecting"
                ? "Updates delayed"
                : transport === "connecting"
                  ? "Connecting"
                  : caughtUp
                    ? "Connected"
                    : "Catching up";
      setStatus((previous) =>
        previous.scope === scope && previous.connection === connection
          ? previous
          : { scope, connection },
      );
    };
    const queue = new SyncQueue(
      async (work) => {
        if (inactive() || !navigator.onLine) return;
        syncingRoom = null;
        if (work.directory) {
          const options = directoryQueryOptions(currentIdentity);
          if (
            client.getQueryState(options.queryKey)?.fetchStatus === "fetching"
          )
            await client.fetchInfiniteQuery(options);
          if (inactive()) return;
          client.setQueryData<InfiniteData<DirectoryPage, string>>(
            options.queryKey,
            (data) =>
              data?.pageParams[0]
                ? { pages: data.pages.slice(0, 1), pageParams: [""] }
                : data,
          );
          await client.invalidateQueries({
            queryKey: options.queryKey,
            exact: true,
            refetchType: "none",
          });
          if (inactive()) return;
          await client.fetchInfiniteQuery(options);
          if (inactive()) return;
          // Contacts share member hints; inactive sheets fetch on opening.
          await client.invalidateQueries(
            {
              queryKey: ["webchat", clientId, memberId, "contacts"],
              refetchType: "active",
            },
            { cancelRefetch: false },
          );
          if (inactive()) return;
          directoryFailed = false;
        }
        const room = work.chatId;
        if (room && room === selected.current && !inactive()) {
          syncingRoom = room;
          const options = chatQueryOptions(client, currentIdentity, room);
          if (
            client.getQueryState(options.queryKey)?.fetchStatus === "fetching"
          )
            await client.fetchQuery(options);
          if (inactive() || room !== selected.current) return;
          await client.invalidateQueries({
            queryKey: options.queryKey,
            exact: true,
            refetchType: "none",
          });
          if (inactive() || room !== selected.current) return;
          const snapshot = await client.fetchQuery(options);
          if (inactive() || room !== selected.current) return;
          accessLost = false;
          if (snapshot.hasMore) {
            caughtUp = false;
            queue.request({ chatId: room });
          } else {
            if (failedRoom === room) failedRoom = null;
            caughtUp = true;
          }
        } else if (!selected.current) caughtUp = true;
      },
      (error) => {
        if (inactive()) return;
        const room = syncingRoom;
        if (room && room !== selected.current) return;
        if (room) failedRoom = room;
        else directoryFailed = true;
        if (
          error instanceof ChatApiError &&
          [401, 403, 404].includes(error.status)
        ) {
          void verifyAccess(room).then((outcome) => {
            if (inactive()) return;
            if (room && room !== selected.current) return;
            if (room) accessLost = outcome === "access-denied";
            update();
          });
        }
        update();
      },
      update,
    );

    const expireSession = (session?: Session) => {
      if (inactive()) return;
      sessionExpired = true;
      transport = "session-expired";
      setKeepAlive(false);
      clearTimeout(timer);
      queue.stop();
      connection.stop();
      useChatStore.getState().clearPrivate();
      void client.cancelQueries({ queryKey: ["webchat", clientId, memberId] });
      client.setQueryData<Session>(
        ["webchat", "session"],
        (previous) =>
          session ?? (previous ? { ...previous, identity: null } : undefined),
      );
      client.removeQueries({
        predicate: (query) =>
          query.queryKey[0] === "webchat" && query.queryKey[1] !== "session",
      });
      update();
    };
    const verifyAccess = async (
      room: string | null,
    ): Promise<"retry" | "session-expired" | "access-denied"> => {
      if (stopped) return "retry";
      if (sessionExpired) return "session-expired";
      try {
        const session = await chatRequest<Session>(
          API_ENDPOINTS.webchat.session,
        );
        if (stopped) return "retry";
        if (sessionExpired) return "session-expired";
        if (
          !session.identity ||
          session.identity.memberId !== memberId ||
          session.identity.clientId !== clientId
        ) {
          expireSession(session);
          return "session-expired";
        }
        if (room) await chatRequest(API_ENDPOINTS.webchat.history(room));
        if (sessionExpired) return "session-expired";
        return "retry";
      } catch (error) {
        if (stopped) return "retry";
        if (sessionExpired) return "session-expired";
        if (error instanceof ChatApiError && error.status === 401) {
          expireSession();
          return "session-expired";
        }
        if (
          room &&
          error instanceof ChatApiError &&
          [403, 404].includes(error.status)
        )
          return "access-denied";
        return "retry";
      }
    };
    const connection = new RealtimeConnection({
      getTicket: (signal) =>
        chatRequest(API_ENDPOINTS.webchat.ticket, "POST", {}, signal),
      onState: (next) => {
        if (inactive()) return;
        if (next === "session-expired") {
          expireSession();
          return;
        }
        transport = next;
        setKeepAlive(next === "ready");
        if (next !== "ready") caughtUp = false;
        update();
      },
      onReady: () => {
        if (inactive()) return;
        caughtUp = false;
        accessLost = false;
        update();
        queue.request({ directory: true, chatId: selected.current });
      },
      onDirectory: () => {
        if (!inactive()) queue.request({ directory: true });
      },
      onChat: (room) => {
        if (inactive() || room !== selected.current) return;
        handlers.current.clearAssistants();
        queue.request({ chatId: room });
      },
      onTyping: (frame) => {
        if (!inactive()) handlers.current.receive(frame);
      },
      onReset: () => handlers.current.clear(),
      verifyAccess,
    });
    controller.current = connection;
    sync.current = queue;
    connection.setChat(selected.current);
    connection.setOnline(navigator.onLine);
    connection.start();

    const reconcile = () => {
      clearTimeout(timer);
      if (inactive() || document.visibilityState === "hidden") return;
      const healthy = transport === "ready" && !syncFailed();
      timer = setTimeout(
        () => {
          if (navigator.onLine && transport !== "session-expired")
            queue.request({
              directory: true,
              chatId: accessLost ? null : selected.current,
            });
          reconcile();
        },
        healthy
          ? 15_000 + Math.random() * 2_000
          : 8_000 + Math.random() * 4_000,
      );
    };
    const online = () => {
      if (inactive()) return;
      connection.setOnline(navigator.onLine);
      if (navigator.onLine)
        queue.request({ directory: true, chatId: selected.current });
      reconcile();
    };
    const visibility = () => {
      if (inactive()) return;
      if (document.visibilityState === "visible") {
        caughtUp = false;
        update();
        connection.refresh();
        queue.request({
          directory: true,
          chatId: accessLost ? null : selected.current,
        });
      }
      reconcile();
    };
    window.addEventListener("online", online);
    window.addEventListener("offline", online);
    document.addEventListener("visibilitychange", visibility);
    reconcile();
    return () => {
      stopped = true;
      setKeepAlive(false);
      clearTimeout(timer);
      queue.stop();
      connection.stop();
      client.cancelQueries({ queryKey: directoryKey(currentIdentity) });
      if (selected.current)
        client.cancelQueries({
          queryKey: chatKey(currentIdentity, selected.current),
        });
      controller.current = null;
      sync.current = null;
      window.removeEventListener("online", online);
      window.removeEventListener("offline", online);
      document.removeEventListener("visibilitychange", visibility);
    };
  }, [enabled, client, clientId, memberId, adventureId, name, scope]);

  const sendTyping = useCallback(
    (active: boolean) => controller.current?.sendTyping(active),
    [],
  );
  return {
    connection: enabled
      ? status.scope === scope
        ? status.connection
        : "Connecting"
      : "Local UI fixture",
    chatId,
    typingMemberIds: enabled ? typing.members : [],
    sendTyping,
  };
}
